import type { SpiffProcessStatus } from "@/api/dto/process-instance";

import { CASE_STATUSES, type CaseStatus } from "./case";

/** 非終了ステータス。draft / inProgress はこれらに展開される。 */
const IN_FLIGHT: readonly SpiffProcessStatus[] = [
  "not_started",
  "user_input_required",
  "waiting",
  "running",
];

const STATUS_MAP: Record<CaseStatus, readonly SpiffProcessStatus[]> = {
  draft: IN_FLIGHT,
  inProgress: IN_FLIGHT,
  // 差し戻しの表れ方はモデルの作りで 2 通りある（deriveCaseStatus と対応）。
  //  - 起案に戻す（デモ 10 フロー・sales-forecast 通常版）… 案件は終わらないので非終了
  //  - 差し戻しで終了する（部署スコープ版）… complete
  // 片方だけを渡すともう片方が一覧から消えるので両方を取る。
  returned: ["complete", ...IN_FLIGHT],
  completed: ["complete"],
  canceled: ["terminated"],
  suspended: ["suspended"],
  error: ["error"],
};

/**
 * UI ステータス → API の process_status 値。
 * draft / returned は生ステータスと 1:1 でないため、API で粗く絞ってから
 * クライアント側で deriveCaseStatus により再絞り込みする必要がある。
 */
export function toSpiffStatuses(
  statuses: readonly CaseStatus[],
): readonly SpiffProcessStatus[] {
  const result: SpiffProcessStatus[] = [];

  for (const status of statuses) {
    for (const raw of STATUS_MAP[status]) {
      if (!result.includes(raw)) result.push(raw);
    }
  }

  return result;
}

/**
 * API だけでは絞り切れないステータスが含まれているか。
 *
 * 判定は STATUS_MAP から導く（どの UI ステータスがどの生ステータスを共有するかは
 * この表が正本なので、表を変えたときに条件を直し忘れない）。
 *
 * 選んでいない状態が、API に渡す生ステータスに相乗りしているときだけ再絞り込みが
 * 必要。相乗りしている相手も選んでいれば API の結果に余計な案件は入らないので、
 * 総数（pagination.total）もそのまま使える。
 */
export function needsClientSideStatusFilter(
  statuses: readonly CaseStatus[],
): boolean {
  if (statuses.length === 0) return false;

  const requested = new Set<SpiffProcessStatus>(toSpiffStatuses(statuses));

  return CASE_STATUSES.some(
    (status) =>
      !statuses.includes(status) &&
      STATUS_MAP[status].some((raw) => requested.has(raw)),
  );
}
